import { normalizeWorkItemKey } from '../../../../../common/ui/quick-find/normalize-work-item-key';
import { isJqlQueryDetected } from '../../../../../common/utils/intent-detection';

/**
 * Stable dismissal key used when the GitHub tip banner is triggered by a
 * Jira GNIN-routing intent signal (e.g. typed JQL, work item key) rather
 * than a literal GitHub keyword. A single sentinel key means a dismissal
 * is permanent for ALL future Jira GNIN intent triggers — the user only
 * has to dismiss once to opt out of intent-based triggering for good
 * (storage tier-1 "permanent" semantics in storage.ts).
 */
export const JIRA_GNIN_INTENT_DISMISS_KEY = 'jira-gnin-intent';

/**
 * Returns the Jira GNIN intent sentinel when the query would route the
 * user to Jira's work-item / NIN destination — either because the query
 * looks like a work item key OR because the intent-detection service
 * (`isJqlQueryDetected`) has already classified it as typed JQL.
 *
 * The JQL branch is driven by `isJqlQueryDetected`, which reads the
 * last `INTENT_DETECTION` classification, so JQL triggering is gated by
 * a backend intent-detection round-trip rather than a synchronous
 * heuristic. This keeps the trigger surface consistent with the rest of
 * the intent router rather than re-inventing a regex parser.
 *
 * Strictly guarded by `primaryProduct === 'jira'`. Returns `null` for any
 * other product so the banner only appears as a contextual nudge inside
 * Jira.
 *
 * Work-item-key detection mirrors the rest of the dialog tree (see
 * `intent-router-work-item` and `use-intent-router-keyboard-preselection`):
 * uses `normalizeWorkItemKey`. We deliberately reuse the existing matchers
 * instead of duplicating their logic.
 */
export const detectJiraGninIntent = (
	query: string,
	primaryProduct: string | undefined | null,
): typeof JIRA_GNIN_INTENT_DISMISS_KEY | null => {
	if (primaryProduct !== 'jira') {
		return null;
	}
	const trimmed = query.trim();
	if (!trimmed) {
		return null;
	}
	const isWorkItemKey = Boolean(normalizeWorkItemKey(trimmed));
	if (isWorkItemKey || isJqlQueryDetected(trimmed)) {
		return JIRA_GNIN_INTENT_DISMISS_KEY;
	}
	return null;
};
